import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, ArrowRight, Shield, Sparkles, Users, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LoginPageProps {
  onBack: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onBack }) => {
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset loading state if user gets logged in
  useEffect(() => {
    if (currentUser) {
      setIsLoading(false);
    }
  }, [currentUser]);

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await login();
      // Don't set isLoading to false here - let the auth state change handle the redirect
    } catch (error: any) {
      setError(error.message || 'Failed to sign in with Google');
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <button onClick={() => navigate('/')} className="back-link">
            ← Back to Home
          </button>
          
          <div className="login-logo">
            <Brain size={40} />
            <span>ShowerThoughts</span>
          </div>
          
          <h1>Welcome Back!</h1>
          <p>Sign in to start transforming your ideas into app concepts</p>
        </div>

        <div className="login-form-container">
          <div className="login-benefits">
            <h3>Why sign in?</h3>
            <div className="benefit-item">
              <Shield size={20} />
              <div>
                <strong>Save Your Ideas</strong>
                <p>Keep all your generated concepts in one place</p>
              </div>
            </div>
            <div className="benefit-item">
              <Sparkles size={20} />
              <div>
                <strong>Unlimited Generation</strong>
                <p>Create as many app concepts as you want</p>
              </div>
            </div>
            <div className="benefit-item">
              <Users size={20} />
              <div>
                <strong>Share & Collaborate</strong>
                <p>Share your concepts with friends and teams</p>
              </div>
            </div>
            <div className="benefit-item">
              <Zap size={20} />
              <div>
                <strong>Advanced Features</strong>
                <p>Access premium AI models and export options</p>
              </div>
            </div>
          </div>

          <div className="login-form">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="google-login-btn"
            >
              <div className="google-icon">
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              {isLoading ? 'Signing in...' : 'Continue with Google'}
              <ArrowRight size={16} />
            </button>

            <div className="login-footer">
              <p>By signing in, you agree to our Terms of Service and Privacy Policy</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;